import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Users, BookOpen, Globe, Award, ArrowRight, CheckCircle } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;
const BE = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return "";
  // Always proxy — handles both relative CRM paths and full S3/CDN URLs
  return `${BE}/api/proxy/gallery-image?path=${encodeURIComponent(url)}`;
};

// Fallback to actual TIPS campus photo (Campus Life tag, IMG_3554.JPG)
const FALLBACK_HEADER = "https://images.pexels.com/photos/8926848/pexels-photo-8926848.jpeg?auto=compress&cs=tinysrgb&w=1920&h=600&fit=crop";
const FALLBACK_SIDE   = `${process.env.REACT_APP_CRM_BACKEND}/api/public/gallery/media/edkonect/gallery/tipsblr/1aa3ed68-9351-4b88-be8b-d4b29273fcf6.jpg`;

const About = () => {
  const [headerImg, setHeaderImg] = useState(FALLBACK_HEADER);
  const [sideImg, setSideImg]     = useState(FALLBACK_SIDE);

  useEffect(() => {
    axios
      .get(`${CRM}/api/public/gallery/tipsblr`, { headers: { "X-API-Key": CRM_KEY } })
      .then((res) => {
        const real = (res.data || []).filter((i) => i.type === "photo");
        // Prefer "Campus Life" tagged photo for the Who We Are side image
        const campusLife = real.filter((i) => i.tag === "Campus Life");
        const sideSource  = campusLife.length > 0 ? campusLife[0] : real[1];
        const headerSource = real.find((i) => i.tag === "Events") || real[0];
        if (headerSource) setHeaderImg(resolveUrl(headerSource.url));
        if (sideSource)   setSideImg(resolveUrl(sideSource.url));
      })
      .catch(() => {/* keep fallbacks */});
  }, []);
  return (
    <main>
      <SEO
        title="About Us | 20+ Years of Excellence"
        description="Learn about The Indian Public School (TIPS) Bengaluru – 20+ years of academic excellence, 6 campuses, 5000+ students. CBSE school rooted in democratic values."
        keywords="about TIPS Bengaluru, Indian Public School history, CBSE school Bangalore, TIPS school vision mission, top school Bangalore"
        canonical="/about"
      />
      {/* Page Header */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{ backgroundImage: `url('${headerImg}')` }}
      >
        <div className="absolute inset-0 bg-[#1A0505]/75"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">About Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
            About Us
          </h1>
          <p className="text-[#D97481] text-lg font-medium italic">
            Our motto is “Fulfilling dreams, one at a time”
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Who We Are</span>
              <h2 className="mt-2 text-3xl font-bold text-gray-900 mb-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
                A Legacy of Academic Excellence
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Indian Public School is an institution with a strong foundation in democratic values. One of the most significant phases for a growing child is their student life at school.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                At TIPS Bengaluru, we ensure children have a well-rounded and enriching student life. Every aspect of a student’s life in school is planned in meticulous detail keeping in mind the needs of children and their overall well-being.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Situated in the northern part of the city, TIPS Bengaluru caters to the crucial learning needs of students. In an era where there is a lot of lacuna between dreams envisioned and realities built, education at TIPS bridges the two.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "20+", desc: "Years of Excellence" },
                  { label: "6", desc: "Campuses in Bengaluru" },
                  { label: "5000+", desc: "Students Enrolled" },
                  { label: "100%", desc: "CBSE Board Results" }
                ].map((s) => (
                  <div key={s.label} className="bg-[#FDF5F6] rounded-xl p-4 border border-[#FEE7E9]">
                    <div className="text-3xl font-bold text-[#9B1522]" style={{ fontFamily: "'Poppins', sans-serif" }}>{s.label}</div>
                    <div className="text-sm text-gray-600 mt-1">{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={sideImg}
                  alt="TIPS Students Learning"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#9B1522] text-white rounded-xl p-5 shadow-xl">
                <div className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>CBSE</div>
                <div className="text-[#F8C5C9] text-xs mt-1">Affiliation No. 830884</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gray-50" id="student-life">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Our Values</span>
            <h2 className="mt-2 text-3xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: "Academic Excellence", desc: "Rigorous academics with innovative pedagogy. Our educators ensure students develop critical thinking, creativity, and a love for learning." },
              { icon: Heart, title: "Holistic Development", desc: "Beyond academics, students develop skills, discover talents, forge personalities, build character, and make friends for life." },
              { icon: Users, title: "Community & Care", desc: "A warm, supportive community where every student is known, valued, and given opportunities to shine." },
              { icon: Globe, title: "Global Perspective", desc: "While rooted in Indian values, we prepare students for a globalised world with international exchange programmes and global exposure." },
              { icon: Award, title: "Safe Environment", desc: "Safety is top priority. Comprehensive protocols ensure Medical Safety, Fire Safety, Electrical Safety and Internet Safety." },
              { icon: CheckCircle, title: "Values & Character", desc: "We instil democratic values, respect for diversity, environmental consciousness and a strong sense of social responsibility." }
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#FDF5F6] rounded-xl flex items-center justify-center mb-4">
                  <Icon size={22} className="text-[#B41C2C]" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 bg-[#1A0505]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="text-4xl text-[#B41C2C] mb-4">“</div>
          <blockquote className="text-white text-2xl font-medium leading-relaxed mb-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Education is for improving the lives of others and for leaving your community and world better than you found it
          </blockquote>
          <p className="text-[#C83C4A] font-semibold">– Marian Wright Edelman</p>
          <p className="text-gray-500 mt-2 text-sm">The guiding philosophy behind everything we do at TIPS Bengaluru</p>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-16 bg-white" id="facilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Facilities</span>
            <h2 className="mt-2 text-3xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>World-Class Infrastructure</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              "Smart Classrooms", "Science Labs", "Computer Labs", "Library",
              "Sports Grounds", "Art & Craft Rooms", "Music Rooms", "Dance Studios",
              "Cafeteria", "Medical Room", "Transport Fleet", "CCTV Surveillance"
            ].map((facility) => (
              <div key={facility} className="bg-[#FDF5F6] border border-[#FEE7E9] rounded-xl p-4 text-center hover:bg-[#FEE7E9] transition-colors">
                <div className="w-8 h-8 bg-[#F8C5C9] rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle size={16} className="text-[#9B1522]" />
                </div>
                <p className="text-sm font-semibold text-gray-800">{facility}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#9B1522]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>Visit Us Today</h2>
          <p className="text-[#FEE7E9] mb-6">Come experience the TIPS difference for yourself.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/admissions" className="bg-white text-[#87111E] font-bold px-8 py-3 rounded-xl hover:bg-gray-100 transition-colors">Apply Online</Link>
            <Link to="/contact" className="border-2 border-white/50 hover:border-white text-white font-bold px-8 py-3 rounded-xl hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;